import React, { useRef, useState } from "react";
import { useAuth } from "../../../core/context/AuthContext";
import { requestMoreUsage } from "../../businessModeService";
import { useAiCreditsUsageOptional } from "./AiCreditsUsageContext";
import { markAiCreditsTopupRequested } from "./aiCreditsTopupNotice";
import { friendlyErrorMessage } from "./userFacingErrors";

/** Sentinel formerly used for inline banners — kept for any leftover callers. */
export const USAGE_LIMIT_REACHED_MESSAGE = "__USAGE_LIMIT_REACHED__";

/**
 * True when a thrown service error is the AI credits 402.
 *
 * @example
 * isUsageLimitError({ code: "USAGE_LIMIT_REACHED" }) // true
 */
export const isUsageLimitError = (err) =>
  err?.code === "USAGE_LIMIT_REACHED" || err?.status === 402;

/**
 * If ``err`` is a usage-limit 402, notify the shared list banner and return true.
 *
 * @example
 * if (reportUsageLimitReached(err)) return;
 * setError(err.message);
 */
export const reportUsageLimitReached = (err) => {
  if (!isUsageLimitError(err)) return false;
  // Lazy import avoidance: callers pass notify, or we fire a DOM event fallback.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("starmesh:ai-credits-usage-limit"));
  }
  return true;
};

/**
 * Map a caught error for local string state. Usage-limit errors return "" so
 * nothing renders inline — the list-level banner handles them.
 *
 * @example
 * usageOrErrorMessage({ code: "USAGE_LIMIT_REACHED" }, "Failed") // ""
 */
export const usageOrErrorMessage = (err, fallback) => {
  if (isUsageLimitError(err)) {
    reportUsageLimitReached(err);
    return "";
  }
  return String(err?.message || fallback);
};

/**
 * Error text for an AI Assist run that failed to start or poll. Never raw: known
 * failures map to a sentence with a next step, anything else to a generic one (the raw
 * text is logged). Credits 402s return "" and notify the list-level banner.
 *
 * @example
 * performerRunErrorMessage(new Error("Failed to fetch run input messages: Source table mapping is missing"))
 * // "We couldn't load the original source for this task. Try again."
 */
export const performerRunErrorMessage = (err) => {
  if (isUsageLimitError(err)) {
    reportUsageLimitReached(err);
    return "";
  }
  return friendlyErrorMessage(err, { context: "ai" });
};

/**
 * Page-level out-of-credits banner (catch-up / aging visual language).
 *
 * Works without AuthProvider (Chrome extension): pass ``userId`` so "Request
 * more credits" can still mark the top-up notice for this user.
 *
 * @example
 * <UsageLimitBanner />
 *
 * @example
 * <UsageLimitBanner userId="c9eb0e33-..." onRequested={refreshSession} />
 */
const UsageLimitBanner = ({ onRequested, userId = "" }) => {
  const auth = useAuth();
  const user = auth?.user;
  const validateSession = auth?.validateSession;
  const usage = useAiCreditsUsageOptional();
  const resolvedUserId = String(userId || user?.id || "").trim();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(""); // "" | "sent" | "error"
  const [errorDetail, setErrorDetail] = useState("");
  const requestInFlightRef = useRef(false);

  const handleRequest = async () => {
    if (busy || status === "sent" || requestInFlightRef.current) {
      return;
    }
    requestInFlightRef.current = true;
    setBusy(true);
    setStatus("");
    setErrorDetail("");
    try {
      await requestMoreUsage();
      markAiCreditsTopupRequested(resolvedUserId);
      setStatus("sent");
      await validateSession?.();
      onRequested?.();
    } catch (err) {
      setStatus("error");
      setErrorDetail(String(err?.message || "Couldn't send request."));
    } finally {
      setBusy(false);
      requestInFlightRef.current = false;
    }
  };

  const handleDismiss = () => {
    usage?.dismissUsageLimitReached?.();
  };

  return (
    <div
      className="action-items-catch-up-banner action-items-catch-up-banner--usage-limit"
      role="status"
      aria-live="polite"
    >      <p className="action-items-catch-up-banner__text">
        {status === "sent" ? (
          <span className="action-items-catch-up-banner__segment">
            We have notified your request to our team
          </span>
        ) : (
          <span className="action-items-catch-up-banner__segment">
            <strong>You&apos;re out of AI credits for now</strong>
            <span className="action-items-catch-up-banner__suffix">
              {" "}
              <button
                type="button"
                className="action-items-catch-up-banner__link"
                onClick={handleRequest}
                disabled={busy}
              >
                {busy ? "Sending…" : "Request more credits"}
              </button>
            </span>
            {status === "error" ? (
              <span className="action-items-catch-up-banner__suffix">
                {" "}
                · {errorDetail || "Couldn't send request."}
              </span>
            ) : null}
          </span>
        )}
      </p>
      <button
        type="button"
        className="action-items-catch-up-banner__close"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

/**
 * Local error renderer — never shows the usage-limit banner inline.
 *
 * @example
 * renderUsageOrError("Failed", "p", "ai-run-details__line")
 */
export const renderUsageOrError = (error, Tag = "p", className = "") => {
  if (!error || error === USAGE_LIMIT_REACHED_MESSAGE) return null;
  return <Tag className={className}>{error}</Tag>;
};

export default UsageLimitBanner;
